import { DOCUMENT } from '@angular/common';
import { inject, Injectable, InjectionToken, NgZone, OnDestroy, signal } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { Subscription } from 'rxjs';

export const PWA_RELOAD = new InjectionToken<() => void>('PWA_RELOAD', {
  providedIn: 'root', factory: () => {
    const document = inject(DOCUMENT);
    return () => document.defaultView?.location.reload();
  }
});

@Injectable({ providedIn: 'root' })
export class PwaUpdateService implements OnDestroy {
  private readonly updates = inject(SwUpdate, { optional: true });
  private readonly document = inject(DOCUMENT);
  private readonly zone = inject(NgZone);
  private readonly reload = inject(PWA_RELOAD);
  private readonly window = this.document.defaultView;
  private readonly subscriptions = new Subscription();
  private started = false;
  private destroyed = false;
  private checking = false;
  private lastCheck = -Infinity;
  private interval?: ReturnType<typeof setInterval>;
  private reminder?: ReturnType<typeof setTimeout>;
  readonly available = signal(false);
  readonly deferred = signal(false);
  readonly recovery = signal(false);
  readonly reloading = signal(false);
  readonly error = signal('');

  private readonly wake = () => { void this.check(); };
  private readonly reconnect = () => { void this.check(true); };

  initialize(): void {
    if (this.started || !this.updates?.isEnabled || !this.window) return;
    this.started = true;
    this.subscriptions.add(this.updates.versionUpdates.subscribe(event => {
      if (event.type === 'VERSION_READY') this.zone.run(() => {
        this.available.set(true);
        this.deferred.set(false);
        this.error.set('');
      });
      // An incomplete download never replaces the running version or offers a reload.
    }));
    this.subscriptions.add(this.updates.unrecoverable.subscribe(() => this.zone.run(() => {
      this.recovery.set(true);
      this.available.set(true);
      this.deferred.set(false);
    })));
    this.document.addEventListener('visibilitychange', this.wake);
    this.window.addEventListener('focus', this.wake);
    this.window.addEventListener('pageshow', this.wake);
    this.window.addEventListener('online', this.reconnect);
    // Wait for registration without adding polling that delays Angular stability.
    void this.window.navigator.serviceWorker?.ready.then(() => {
      if (this.destroyed) return;
      this.zone.runOutsideAngular(() => {
        void this.check(true);
        this.interval = setInterval(this.wake, 5 * 60 * 1000);
      });
    }).catch(() => { /* Continue using the app if registration is unavailable. */ });
  }

  async check(force = false): Promise<void> {
    if (!this.updates?.isEnabled || this.destroyed || this.checking || !this.window ||
        this.document.visibilityState === 'hidden' || !this.window.navigator.onLine ||
        (!force && Date.now() - this.lastCheck < 60000)) return;
    this.checking = true;
    this.lastCheck = Date.now();
    try { await this.updates.checkForUpdate(); }
    catch { /* Offline or incomplete deployment: retain the current working version. */ }
    finally { this.checking = false; }
  }

  apply(): void {
    if (!this.available() || this.reloading() || !this.window) return;
    if (!this.window.navigator.onLine) {
      this.error.set('Volvé a conectarte para actualizar. Podés seguir usando esta versión.');
      return;
    }
    if (this.document.querySelector('form.ng-dirty, [data-unsaved-changes="true"]') &&
        !this.window.confirm('Hay cambios sin guardar. ¿Querés recargar para actualizar de todos modos?')) return;
    this.reloading.set(true);
    // Reload the whole app to avoid mixing old lazy chunks with the new app shell.
    // Do not clear storage, unregister workers or delete caches used by other tabs.
    this.reload();
  }

  later(): void {
    this.deferred.set(true);
    clearTimeout(this.reminder);
    this.zone.runOutsideAngular(() => {
      this.reminder = setTimeout(() => this.zone.run(() => this.deferred.set(false)), 15 * 60 * 1000);
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.unsubscribe();
    clearInterval(this.interval);
    clearTimeout(this.reminder);
    this.document.removeEventListener('visibilitychange', this.wake);
    this.window?.removeEventListener('focus', this.wake);
    this.window?.removeEventListener('pageshow', this.wake);
    this.window?.removeEventListener('online', this.reconnect);
  }
}
